import { AxiosService, getApiErrorMessage } from './axios.service'
import { StorageService } from './storage.service'

const baseURL = 'venda-de-passagem'

// Get with optional keep-latest: with a key, a newer call with the same key cancels the one in flight
const getMuviHubLocalities = (cancelKey, url, config) =>
	(cancelKey
		? AxiosService.getKeepLatestMuviHub(cancelKey, url, config)
		: AxiosService.getMuviHub(url, config)
	).then(({ data }) => data)

/**
 * Defines the API methods for ticket sale
 */
export class TicketSaleService {
	/**
	 * Get the origins matching a search term
	 * @param {string} query The search term
	 * @param {string} [cancelKey] Keep-latest key: a newer call with the same key cancels this one
	 * @return {Promise<any>} The list of origins
	 */
	static getOrigins(query, cancelKey) {
		return getMuviHubLocalities(cancelKey, 'travel/origins', {
			params: {
				query,
				context: 'standard',
				limit: 30,
			},
		})
	}

	/**
	 * Get the destinations matching an origin and, optionally, a search term
	 * @param {string} originId The origin locality id
	 * @param {string} [query] The search term
	 * @param {string} [cancelKey] Keep-latest key: a newer call with the same key cancels this one
	 * @return {Promise<any>} The list of destinations
	 */
	static getDestinations(originId, query, cancelKey) {
		return getMuviHubLocalities(cancelKey, `travel/origins/${originId}/destinations`, {
			params: {
				query,
				context: 'standard',
			},
		})
	}

	/**
	 * Swap the origin and destination
	 * @return {Promise<any>} The new origin, destination and available destinations
	 */
	static swapLocalities({ currentOriginId, currentDestinationId }) {
		return AxiosService.postMuviHub(
			'travel/swap',
			{ currentOriginId, currentDestinationId },
			{ params: { context: 'standard' } }
		).then(({ data }) => data)
	}

	/**
	 * Search the trips
	 * @return {Promise<any>} The outbound and return trips
	 */
	static async searchTrips({
		originId,
		destinationId,
		tripDateGoing,
		tripDateReturn,
	}) {
		const { data } = await AxiosService.getKeepLatestMuviHub('trips-search', 'trips/search', {
			params: {
				originId,
				destinationId,
				departureDate: tripDateGoing,
				returnDate: tripDateReturn,
			},
		})

		return data.data
	}

	/**
	 * Get the bus seats
	 * @param {object} params
	 * shape {
	 *  "busToken": "string",
	 *  "direction": "outbound",
	 *  "kind": "outbound",
	 *  "legIndex": 0,
	 *  "selectedPriceId": 0,
	 *  "withInsurance": false
	 * }
	 * @return {Promise<any>} The bus seats information
	 */
	static getBusData(params, requestId) {
		return AxiosService.postKeepFirstMuviHub(
			requestId,
			'trips/seat-map',
			params
		)
			.then(({ data }) => data)
			// Spec 055 (T087/D29): recusa do Hub (ex.: carrinho que misturaria empresas que precisam de pedidos
			// separados) chega como erro HTTP. Na tela de compra o aviso automático é silenciado, então a mensagem
			// do Hub volta como resultado "sem sucesso" para quem chamou mostrar ao cliente.
			.catch((error) => ({
				success: false,
				data: null,
				message: getApiErrorMessage(error) || null,
			}))
	}

	/**
	 * Finalize the purchase
	 * @return {Promise<any} the purchase result
	 */
	static finalizePurchase(params) {
		return AxiosService.post(`${baseURL}/confirmar-venda`, params).then(
			({ data }) => data
		)
	}

	/**
	 * Book a bus seat
	 * @return {Promise<any} the booking result, shape { reservation, cart }
	 */
	static bookBusSeat(params) {
		const cart = StorageService.cart.get()

		return AxiosService.postMuviHub(`carts/${cart && cart.id}/seats`, params).then(
			({ data }) => data
		)
	}

	/**
	 * Cancel a bus seat booking
	 * @param {object} params Must include transactionId, the reservation id returned by bookBusSeat
	 * @return {Promise<any} the cancelation result
	 */
	static cancelBusSeatBooking(params) {
		const cart = StorageService.cart.get()

		return AxiosService.deleteMuviHub(
			`carts/${cart && cart.id}/seats/${params.transactionId}`
		).then(({ data }) => data)
	}

	/**
	 * Request for the authenticator form
	 * @return {Promise<any} the purchase result
	 */
	static sendGetNetDebitCard(params) {
		return AxiosService.post(
			`${baseURL}/formulario-autenticador`,
			params
		).then(({ data }) => data)
	}

	/**
	 * Submit the PaRes
	 * @return {Promise<any} the purchase result
	 */
	static confirmPaResDebitCard(params) {
		return AxiosService.post(
			`gateway-getnet/confirmar-pagamento-debito`,
			params
		).then(({ data }) => data)
	}

	/**
	 * Get parameters to family discount
	 * @return {Promise<any} the family discount parameters return
	 */
	static getDiscountParameters() {
		return AxiosService.get(`${baseURL}/parametros`).then(({ data }) => data)
	}

	/**
	 * Get bankslip parameters
	 * @return {Promise<any} bankslip parameters
	 */
	static getBankSlipParameters() {
		return AxiosService.get(`${baseURL}/boleto-parametros`).then(
			({ data }) => data
		)
	}

	/**
	 * Get muvicard discount
	 * @return {Promise<any} the family discount parameters return
	 */
	static getMuvicardDiscountParameters(params) {
		return AxiosService.post(`${baseURL}/muvicard-parametros`, params).then(
			({ data }) => data
		)
	}

	/**
	 * Applies the best automatic coupon available for the cart (Bug 7829).
	 * POST with no body - the cart id in the route and the auth header (when
	 * logged in) are the only real inputs; the API cascades user-based then
	 * route-based coupon resolution server-side.
	 * @return {Promise<any}
	 */
	static getAutomaticDiscountCoupon() {
		const cart = StorageService.cart.get()

		return AxiosService.postMuviHub(`carts/${cart && cart.id}/coupon/automatic`).then(
			({ data }) => data
		)
	}
}

export default TicketSaleService
